import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { createUserProfile, getUserProfile } from '../utils/database';
import '../styles/AuthPage.css';
import Header from '../components/Header';
import quotesData from '../data/quotes.json';
import AuthFooter from '../components/AuthFooter';

function AuthPage({ mode = 'signup' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isSignUp = mode === 'signup' || location.pathname === '/signup';
  const searchParams = new URLSearchParams(location.search);
  const clubSlug = searchParams.get('club');
  const coachSlug = searchParams.get('coach');
  const redirectTo = searchParams.get('redirect');
  
  // Form state
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Random quote - changes on every render/navigation
  const [randomQuote, setRandomQuote] = useState(() => {
    const randomIndex = Math.floor(Math.random() * quotesData.length);
    return quotesData[randomIndex];
  });

  // Change quote and reset state when switching between signup/signin
  React.useEffect(() => {
    const randomIndex = Math.floor(Math.random() * quotesData.length);
    setRandomQuote(quotesData[randomIndex]);
    setResetSent(false);
    setError('');
  }, [location.pathname]);

  // Validation
  const isPasswordValid = password.length >= 12;
  const isEmailValid = email.includes('@') && email.includes('.');
  const isFormValid = isSignUp 
    ? isEmailValid && firstName.trim() && lastName.trim() && isPasswordValid && agreeToTerms
    : isEmailValid && isPasswordValid;

  // Store credentials using Web Credentials API for PWA autofill/FaceID support
  const storeCredentials = async (email, password) => {
    try {
      if ('credentials' in navigator && 'PasswordCredential' in window) {
        // eslint-disable-next-line no-undef
        const cred = new window.PasswordCredential({
          id: email,
          password: password,
          name: email
        });
        await navigator.credentials.store(cred);
        console.log('[Auth] Credentials stored for PWA autofill');
      }
    } catch (err) {
      // Silently fail - credential storage is optional enhancement
      console.log('[Auth] Credential storage not available:', err.message);
    }
  };

  const handleResetPassword = async () => {
    if (!email || !isEmailValid) {
      setError('Please enter your email address above, then try again.');
      return;
    }
    setError('');
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      switch (err.code) {
        case 'auth/user-not-found':
          // Don't reveal whether account exists for security
          setResetSent(true);
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/too-many-requests':
          setError('Too many requests. Please wait a moment and try again.');
          break;
        default:
          setError('Something went wrong. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        // Create Firebase Auth user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Create Firestore profile
        await createUserProfile(user.uid, {
          email,
          displayName: `${firstName} ${lastName}`,
          firstName,
          lastName,
          termsAccepted: true,
          termsAcceptedAt: new Date(),
          termsVersion: '2025-01-01'
        });

        // Store credentials for PWA autofill
        await storeCredentials(email, password);

        // Build navigation state (carry club/coach slug and redirect through)
        const navState = {};
        if (clubSlug) navState.clubSlug = clubSlug;
        if (coachSlug) navState.coachSlug = coachSlug;
        if (redirectTo) navState.redirect = redirectTo;
        const stateArg = Object.keys(navState).length ? navState : undefined;

        navigate('/onboarding', { state: stateArg });
      } else {
        // Sign in
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await storeCredentials(email, password);

        const profile = await getUserProfile(user.uid);
        if (profile?.onboardingComplete) {
          if (redirectTo) {
            navigate(redirectTo);
          } else if (clubSlug || coachSlug) {
            navigate(`/${user.uid}/dashboard`, {
              state: {
                ...(clubSlug ? { pendingClubSlug: clubSlug } : {}),
                ...(coachSlug ? { pendingCoachSlug: coachSlug } : {}),
              },
            });
          } else if (profile.role === 'club_owner' && !profile.ownedClubIds?.length) {
            navigate('/clubs/signup');
          } else if (profile.role === 'coach' && !profile.coachId) {
            navigate('/coach/signup');
          } else {
            navigate(`/${user.uid}/dashboard`);
          }
        } else {
          const navState = {};
          if (clubSlug) navState.clubSlug = clubSlug;
          if (coachSlug) navState.coachSlug = coachSlug;
          if (redirectTo) navState.redirect = redirectTo;
          navigate('/onboarding', { state: Object.keys(navState).length ? navState : undefined });
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/weak-password':
          setError('Password must be at least 12 characters.');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('Invalid email or password.');
          break;
        default:
          setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Debug logging
  console.log('[AuthPage] Rendering - mode:', isSignUp ? 'signup' : 'signin');
  console.log('[AuthPage] Form valid:', isFormValid);

  return (
    <div className="auth-page">
      {/* Site Navigation */}
      <Header />

      {/* Background effects */}
      <div className="auth-bg-glow auth-bg-glow-1" />
      <div className="auth-bg-glow auth-bg-glow-2" />
      <div className="auth-bg-overlay" />

      {/* Quote section */}
      <div className="auth-quote-section">
        <p className="auth-quote">"{randomQuote.quote}"</p>
        <p className="auth-quote-author">— {randomQuote.author}</p>
      </div>

      {/* Auth form card */}
      <div className="auth-card">
        {/* Tab switcher */}
        <div className="auth-tabs">
          <Link 
            to={`/signup${location.search}`} 
            className={`auth-tab ${isSignUp ? 'active' : ''}`}
          >
            Sign Up
          </Link>
          <Link 
            to={`/signin${location.search}`} 
            className={`auth-tab ${!isSignUp ? 'active' : ''}`}
          >
            Sign In
          </Link>
        </div>

        <form 
          onSubmit={handleSubmit} 
          className="auth-form" 
          autoComplete="on"
          method="post"
          action="#"
        >
          {/* Email - use username webauthn for better PWA autofill/FaceID support */}
          <div className="auth-input-wrapper">
            <input
              type="email"
              name="email"
              id="email"
              autoComplete={isSignUp ? 'email' : 'username email webauthn'}
              inputMode="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              required
            />
          </div>

          {/* First Name (Sign Up only) */}
          {isSignUp && (
            <div className="auth-input-wrapper">
              <input
                type="text"
                id="firstName"
                name="firstName"
                autoComplete="given-name"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="auth-input"
                required
              />
            </div>
          )}

          {/* Last Name (Sign Up only) */}
          {isSignUp && (
            <div className="auth-input-wrapper">
              <input
                type="text"
                id="lastName"
                name="lastName"
                autoComplete="family-name"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="auth-input"
                required
              />
            </div>
          )}

          {/* Password - webauthn hint for better FaceID/TouchID support in PWA */}
          <div className="auth-input-wrapper auth-password-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              autoComplete={isSignUp ? 'new-password' : 'current-password webauthn'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              required
            />
            <button
              type="button"
              className="auth-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          <p className={`auth-password-hint ${isPasswordValid ? 'valid' : ''}`}>
            {isPasswordValid ? (
              <>
                <svg className="auth-check-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                12+ characters
              </>
            ) : (
              '12+ characters required'
            )}
          </p>

          {/* Forgot password link (Sign In only) */}
          {!isSignUp && (
            <div className="auth-forgot-password">
              {resetSent ? (
                <p className="auth-reset-success">
                  <svg className="auth-check-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Check your email for a reset link
                </p>
              ) : (
                <button
                  type="button"
                  className="auth-forgot-link"
                  onClick={handleResetPassword}
                  disabled={resetLoading}
                >
                  {resetLoading ? 'Sending...' : 'Forgot password?'}
                </button>
              )}
            </div>
          )}

          {/* Terms checkbox (Sign Up only) - Liability Waiver is handled in Onboarding Step 4 */}
          {isSignUp && (
            <label className="auth-terms">
              <input
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="auth-checkbox"
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="auth-terms-link">
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="auth-terms-link">
                  Privacy Policy
                </Link>
              </span>
            </label>
          )}

          {/* Error message */}
          {error && <p className="auth-error">{error}</p>}

          {/* Submit button */}
          <button
            type="submit"
            className="auth-submit"
            disabled={!isFormValid || loading}
          >
            {loading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
          </button>
        </form>
      </div>

      {/* Bottom tagline */}
      <p className="auth-tagline">
        Begin Strength helps you begin, and finish what you've begun, so you can begin again.
      </p>

      {/* Footer */}
      <AuthFooter />
    </div>
  );
}

export default AuthPage;

